import React, { useEffect, useState } from "react";
import AdminLayout from "../../components/admin/AdminLayout";
import { api, formatApiError } from "../../lib/api";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminVehicles() {
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [trims, setTrims] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  const [activeBrand, setActiveBrand] = useState("");
  const [activeModel, setActiveModel] = useState("");

  const [newBrand, setNewBrand] = useState("");
  const [newModel, setNewModel] = useState("");
  const [newTrim, setNewTrim] = useState("");
  const [newYear, setNewYear] = useState(new Date().getFullYear());
  const [newVehicleTrim, setNewVehicleTrim] = useState("");

  const loadBrands = async () => {
    const { data } = await api.get("/admin/brands");
    setBrands(data.brands);
  };
  const handleGroupChange = async (brand_id, group) => {
    try {
      await api.patch(`/admin/brands/${brand_id}/group`, { brand_group: group });
      loadBrands();
      toast.success("Brand group updated");
    } catch {
      toast.error("Failed to update brand group");
    }
  };
  const loadModels = async (brand_id) => {
    if (!brand_id) { setModels([]); return; }
    const { data } = await api.get(`/admin/models?brand_id=${brand_id}`);
    setModels(data.models);
  };
  const loadTrims = async (model_id) => {
    if (!model_id) { setTrims([]); return; }
    const { data } = await api.get(`/admin/trims?model_id=${model_id}`);
    setTrims(data.trims);
  };
  const loadVehicles = async (brand_id, model_id) => {
    const params = new URLSearchParams();
    if (brand_id) params.set("brand_id", brand_id);
    if (model_id) params.set("model_id", model_id);
    const { data } = await api.get(`/admin/vehicles?${params.toString()}`);
    setVehicles(data.vehicles);
  };

  useEffect(() => { loadBrands(); }, []);
  useEffect(() => { loadModels(activeBrand); loadVehicles(activeBrand, activeModel); setActiveModel(""); /* eslint-disable-next-line */ }, [activeBrand]);
  useEffect(() => { loadTrims(activeModel); loadVehicles(activeBrand, activeModel); /* eslint-disable-next-line */ }, [activeModel]);

  const handleAddBrand = async () => {
    if (!newBrand.trim()) return;
    try {
      await api.post("/admin/brands", { name: newBrand.trim(), is_exotic: false });
      setNewBrand("");
      toast.success("Brand added");
      loadBrands();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const handleAddModel = async () => {
    if (!newModel.trim() || !activeBrand) { toast.error("Select a brand first"); return; }
    try {
      await api.post("/admin/models", { brand_id: activeBrand, name: newModel.trim() });
      setNewModel("");
      toast.success("Model added");
      loadModels(activeBrand);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const handleAddTrim = async () => {
    if (!newTrim.trim() || !activeModel) { toast.error("Select a model first"); return; }
    try {
      await api.post("/admin/trims", { model_id: activeModel, name: newTrim.trim() });
      setNewTrim("");
      toast.success("Trim added");
      loadTrims(activeModel);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const handleAddVehicle = async () => {
    if (!activeBrand || !activeModel || !newYear) { toast.error("Select brand, model, year"); return; }
    try {
      await api.post("/admin/vehicles", {
        year: parseInt(newYear),
        brand_id: activeBrand,
        model_id: activeModel,
        trim_id: newVehicleTrim || null,
      });
      toast.success("Vehicle record added");
      loadVehicles(activeBrand, activeModel);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleDelete = async (kind, id) => {
    if (!window.confirm(`Delete this ${kind}? This will cascade to dependent records.`)) return;
    try {
      await api.delete(`/admin/${kind}/${id}`);
      toast.success("Deleted");
      if (kind === "brands") { loadBrands(); setActiveBrand(""); }
      if (kind === "models") { loadModels(activeBrand); setActiveModel(""); }
      if (kind === "trims") { loadTrims(activeModel); }
      if (kind === "vehicles") { loadVehicles(activeBrand, activeModel); }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AdminLayout title="Vehicles">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Brands */}
        <Panel title="Brands" data-testid="vehicles-brands-panel">
          <div className="flex gap-2 mb-3">
            <input data-testid="add-brand-input" value={newBrand} onChange={(e) => setNewBrand(e.target.value)} placeholder="New brand" className={inputCls} />
            <button data-testid="add-brand-btn" onClick={handleAddBrand} className="px-3 bg-zinc-900 text-white text-xs"><Plus size={12} /></button>
          </div>
          <div className="border border-zinc-200 rounded max-h-[480px] overflow-y-auto">
            {brands.map((b) => (
              <button
                key={b.id}
                data-testid={`brand-row-${b.slug}`}
                onClick={() => setActiveBrand(b.id)}
                className={`w-full text-left px-3 py-2 border-b border-zinc-100 flex items-center justify-between text-sm hover:bg-zinc-50 ${activeBrand === b.id ? "bg-zinc-100" : ""}`}
              >
                <div>
                  <div>{b.name}</div>
                  {b.is_exotic && <div className="text-[9px] uppercase tracking-[0.2em] text-red-600 font-bold">Exotic</div>}
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <select
                    data-testid={`brand-group-select-${b.slug}`}
                    value={b.brand_group || (b.is_exotic ? "exotic_luxury" : "other")}
                    onChange={(e) => handleGroupChange(b.id, e.target.value)}
                    className="text-[10px] border border-zinc-200 rounded px-1.5 py-1 bg-white"
                  >
                    <option value="exotic_luxury">Exotic &amp; Luxury</option>
                    <option value="ev_specialty">EV Specialty</option>
                    <option value="other">Other</option>
                  </select>
                  {b.custom && <button onClick={(e) => { e.stopPropagation(); handleDelete("brands", b.id); }} className="text-zinc-400 hover:text-red-600"><Trash2 size={12} /></button>}
                </div>
              </button>
            ))}
          </div>
        </Panel>

        {/* Models */}
        <Panel title="Models">
          <div className="flex gap-2 mb-3">
            <input data-testid="add-model-input" value={newModel} onChange={(e) => setNewModel(e.target.value)} placeholder="New model" disabled={!activeBrand} className={inputCls} />
            <button data-testid="add-model-btn" onClick={handleAddModel} disabled={!activeBrand} className="px-3 bg-zinc-900 text-white text-xs disabled:opacity-30"><Plus size={12} /></button>
          </div>
          <div className="border border-zinc-200 rounded max-h-[480px] overflow-y-auto">
            {!activeBrand && <div className="text-xs text-zinc-500 p-3">Select a brand to manage its models.</div>}
            {models.map((m) => (
              <button
                key={m.id}
                onClick={() => setActiveModel(m.id)}
                className={`w-full text-left px-3 py-2 border-b border-zinc-100 flex items-center justify-between text-sm hover:bg-zinc-50 ${activeModel === m.id ? "bg-zinc-100" : ""}`}
              >
                <span>{m.name}</span>
                {m.custom && <button onClick={(e) => { e.stopPropagation(); handleDelete("models", m.id); }} className="text-zinc-400 hover:text-red-600"><Trash2 size={12} /></button>}
              </button>
            ))}
          </div>
        </Panel>

        {/* Trims */}
        <Panel title="Trims">
          <div className="flex gap-2 mb-3">
            <input data-testid="add-trim-input" value={newTrim} onChange={(e) => setNewTrim(e.target.value)} placeholder="New trim" disabled={!activeModel} className={inputCls} />
            <button data-testid="add-trim-btn" onClick={handleAddTrim} disabled={!activeModel} className="px-3 bg-zinc-900 text-white text-xs disabled:opacity-30"><Plus size={12} /></button>
          </div>
          <div className="border border-zinc-200 rounded max-h-[200px] overflow-y-auto mb-4">
            {!activeModel && <div className="text-xs text-zinc-500 p-3">Select a model to manage its trims.</div>}
            {trims.map((t) => (
              <div key={t.id} className="px-3 py-2 border-b border-zinc-100 flex items-center justify-between text-sm">
                <span>{t.name}</span>
                {t.custom && <button onClick={() => handleDelete("trims", t.id)} className="text-zinc-400 hover:text-red-600"><Trash2 size={12} /></button>}
              </div>
            ))}
          </div>

          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2">Add Y/M/M/T record</div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <input data-testid="add-vehicle-year" type="number" min={1980} max={2099} value={newYear} onChange={(e) => setNewYear(e.target.value)} className={inputCls} />
            <select data-testid="add-vehicle-trim" value={newVehicleTrim} onChange={(e) => setNewVehicleTrim(e.target.value)} className={inputCls}>
              <option value="">Any trim</option>
              {trims.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <button data-testid="add-vehicle-btn" onClick={handleAddVehicle} disabled={!activeModel} className="w-full bg-zinc-900 text-white text-xs h-9 disabled:opacity-30">Add vehicle</button>
        </Panel>
      </div>

      {/* Vehicle records table */}
      <div className="bg-white border border-zinc-200 rounded-md mt-8" data-testid="vehicles-table">
        <div className="px-5 py-3 border-b border-zinc-200 text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold flex items-center justify-between">
          <span>Vehicle records ({vehicles.length})</span>
          {activeBrand && <button onClick={() => { setActiveBrand(""); setActiveModel(""); loadVehicles(); }} className="text-xs">Show all</button>}
        </div>
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 bg-zinc-50">
            <tr>
              <th className="text-left px-5 py-2">Year</th>
              <th className="text-left px-5 py-2">Make</th>
              <th className="text-left px-5 py-2">Model</th>
              <th className="text-left px-5 py-2">Trim</th>
              <th className="text-left px-5 py-2">ID</th>
              <th className="text-right px-5 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {vehicles.slice(0, 200).map((v) => (
              <tr key={v.id} className="border-t border-zinc-100">
                <td className="px-5 py-2 font-mono">{v.year}</td>
                <td className="px-5 py-2">{v.make}</td>
                <td className="px-5 py-2">{v.model}</td>
                <td className="px-5 py-2 text-zinc-500">{v.trim || "—"}</td>
                <td className="px-5 py-2 font-mono text-xs text-zinc-500">{v.id.slice(0, 8)}</td>
                <td className="px-5 py-2 text-right">
                  <button onClick={() => handleDelete("vehicles", v.id)} className="text-zinc-400 hover:text-red-600"><Trash2 size={12} /></button>
                </td>
              </tr>
            ))}
            {vehicles.length === 0 && (
              <tr><td colSpan={6} className="text-center text-zinc-500 py-10">No vehicles for this selection.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}

const inputCls = "flex-1 w-full h-9 px-2 text-sm border border-zinc-300 focus:border-zinc-900 focus:outline-none bg-white rounded-sm";
function Panel({ title, children }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-5">
      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-4">{title}</div>
      {children}
    </div>
  );
}
